<template>
  <div class="work-gallery">
    <div v-if="filterable" class="gallery-filters" role="group" aria-label="Filter work by division">
      <button
        v-for="category in workCategories"
        :key="category.key"
        type="button"
        class="gallery-filter"
        :class="{ active: activeCategory === category.key }"
        :aria-pressed="activeCategory === category.key"
        @click="activeCategory = category.key"
      >
        {{ category.label }}
        <span>{{ countFor(category.key) }}</span>
      </button>
    </div>

    <ul class="gallery-grid">
      <li
        v-for="(item, index) in visiblePhotos"
        :key="item.id"
        :class="['gallery-item', `is-${item.orientation}`]"
      >
        <button type="button" class="gallery-tile" @click="open(index)">
          <img :src="item.src" :alt="item.alt" loading="lazy" />

          <span class="gallery-meta">
            <span class="gallery-service">{{ item.service }}</span>
            <span v-if="item.location" class="gallery-location">
              <MapPin />
              {{ item.location }}
            </span>
          </span>

          <span class="gallery-zoom" aria-hidden="true">
            <Maximize2 />
          </span>
        </button>

        <p class="gallery-caption">{{ item.caption }}</p>
      </li>
    </ul>

    <!-- ============ LIGHTBOX ============ -->
    <transition name="lightbox">
      <div
        v-if="lightboxIndex !== null"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="activePhoto.service"
        @click.self="close"
      >
        <button type="button" class="lightbox-close" aria-label="Close" @click="close">
          <X />
        </button>

        <button
          type="button"
          class="lightbox-nav prev"
          aria-label="Previous photo"
          @click="step(-1)"
        >
          <ChevronLeft />
        </button>

        <figure class="lightbox-figure">
          <img :src="activePhoto.src" :alt="activePhoto.alt" />

          <figcaption>
            <span class="lightbox-service">{{ lightboxTitle }}</span>
            <p>{{ activePhoto.caption }}</p>
            <span class="lightbox-count">
              {{ lightboxIndex + 1 }} / {{ visiblePhotos.length }}
            </span>
          </figcaption>
        </figure>

        <button type="button" class="lightbox-nav next" aria-label="Next photo" @click="step(1)">
          <ChevronRight />
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { ChevronLeft, ChevronRight, Maximize2, MapPin, X } from "lucide-vue-next";

import { workCategories, workPhotos } from "@/content/gallery";

export default {
  name: "WorkGallery",

  components: {
    ChevronLeft,
    ChevronRight,
    Maximize2,
    MapPin,
    X,
  },

  props: {
    /** Show the division filter row. */
    filterable: { type: Boolean, default: true },

    /** Restrict the gallery to one division, e.g. "rope-access". */
    category: { type: String, default: "all" },
  },

  data() {
    return {
      workCategories,
      activeCategory: this.category,
      lightboxIndex: null,
    };
  },

  computed: {
    /** Photos in the current division, before the filter row is applied. */
    scopedPhotos() {
      if (this.category === "all") {
        return workPhotos;
      }

      return workPhotos.filter((item) => item.category === this.category);
    },

    visiblePhotos() {
      if (this.activeCategory === "all") {
        return this.scopedPhotos;
      }

      return this.scopedPhotos.filter((item) => item.category === this.activeCategory);
    },

    activePhoto() {
      return this.visiblePhotos[this.lightboxIndex];
    },

    lightboxTitle() {
      const { service, location } = this.activePhoto;
      return location ? `${service} · ${location}` : service;
    },
  },

  watch: {
    // Filtering while the lightbox is open would leave it pointing at the wrong
    // photo, so close it.
    activeCategory() {
      this.close();
    },
  },

  beforeUnmount() {
    this.releaseScroll();
    window.removeEventListener("keydown", this.handleKey);
  },

  methods: {
    countFor(key) {
      if (key === "all") {
        return this.scopedPhotos.length;
      }

      return this.scopedPhotos.filter((item) => item.category === key).length;
    },

    open(index) {
      this.lightboxIndex = index;
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", this.handleKey);
    },

    close() {
      if (this.lightboxIndex === null) {
        return;
      }

      this.lightboxIndex = null;
      this.releaseScroll();
      window.removeEventListener("keydown", this.handleKey);
    },

    step(delta) {
      const total = this.visiblePhotos.length;
      this.lightboxIndex = (this.lightboxIndex + delta + total) % total;
    },

    handleKey(event) {
      if (event.key === "Escape") {
        this.close();
      } else if (event.key === "ArrowRight") {
        this.step(1);
      } else if (event.key === "ArrowLeft") {
        this.step(-1);
      }
    },

    releaseScroll() {
      document.body.style.overflow = "";
    },
  },
};
</script>

<style scoped>
/* ---------- filters ---------- */

.gallery-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 34px;
}

.gallery-filter {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-soft);
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition:
    border-color 180ms ease,
    color 180ms ease,
    background 180ms ease;
}

.gallery-filter span {
  color: var(--muted-light);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.gallery-filter:hover {
  border-color: var(--blue);
  color: var(--blue);
}

.gallery-filter.active {
  border-color: var(--blue);
  background: var(--blue);
  color: #ffffff;
}

.gallery-filter.active span {
  color: rgba(255, 255, 255, 0.7);
}

/* ---------- grid ---------- */

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.gallery-item {
  display: flex;
  flex-direction: column;
}

/* Landscape shots get two columns so they are not crushed into a portrait crop. */
.gallery-item.is-landscape {
  grid-column: span 2;
}

.gallery-tile {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--navy);
  box-shadow: var(--shadow-card);
  cursor: zoom-in;
  transition:
    border-color 220ms ease,
    box-shadow 220ms ease,
    transform 220ms ease;
}

.gallery-tile:hover {
  transform: translateY(-4px);
  border-color: rgba(11, 93, 187, 0.4);
  box-shadow: var(--shadow-lift);
}

.gallery-tile img {
  display: block;
  width: 100%;
  height: 340px;
  object-fit: cover;
  transition: transform 600ms ease;
}

.is-landscape .gallery-tile img {
  height: 340px;
}

.gallery-tile:hover img {
  transform: scale(1.04);
}

.gallery-tile::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(6, 18, 38, 0) 42%,
    rgba(6, 18, 38, 0.82) 100%
  );
}

.gallery-meta {
  position: absolute;
  z-index: 2;
  right: 18px;
  bottom: 16px;
  left: 18px;
  display: grid;
  gap: 7px;
  text-align: left;
}

.gallery-service {
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.gallery-location {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #a9c9ec;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.gallery-location svg {
  flex: 0 0 12px;
  width: 12px;
  height: 12px;
}

.gallery-zoom {
  position: absolute;
  z-index: 2;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  background: rgba(8, 25, 46, 0.42);
  color: #ffffff;
  opacity: 0;
  transition: opacity 200ms ease;
}

.gallery-zoom svg {
  width: 15px;
  height: 15px;
}

.gallery-tile:hover .gallery-zoom,
.gallery-tile:focus-visible .gallery-zoom {
  opacity: 1;
}

.gallery-caption {
  margin: 14px 2px 0;
  color: var(--muted);
  font-size: 12.5px;
  line-height: 1.7;
}

/* ---------- lightbox ---------- */

.lightbox {
  position: fixed;
  z-index: 200;
  inset: 0;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 64px;
  align-items: center;
  gap: 12px;
  padding: 32px;
  background: rgba(4, 13, 26, 0.93);
  backdrop-filter: blur(6px);
}

.lightbox-figure {
  display: grid;
  justify-items: center;
  margin: 0;
  max-height: 100%;
}

.lightbox-figure img {
  max-width: 100%;
  max-height: calc(100vh - 210px);
  object-fit: contain;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.lightbox-figure figcaption {
  max-width: 640px;
  margin-top: 20px;
  text-align: center;
}

.lightbox-service {
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.lightbox-figure figcaption p {
  margin: 10px 0 0;
  color: #a8c0da;
  font-size: 13px;
  line-height: 1.75;
}

.lightbox-count {
  display: block;
  margin-top: 14px;
  color: #6a89ab;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.lightbox-close,
.lightbox-nav {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  cursor: pointer;
  transition:
    border-color 180ms ease,
    background 180ms ease;
}

.lightbox-close:hover,
.lightbox-nav:hover {
  border-color: var(--blue-light);
  background: rgba(76, 155, 240, 0.2);
}

.lightbox-close svg,
.lightbox-nav svg {
  width: 20px;
  height: 20px;
}

.lightbox-close {
  position: absolute;
  top: 26px;
  right: 26px;
}

.lightbox-nav.prev {
  justify-self: start;
}

.lightbox-nav.next {
  justify-self: end;
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 200ms ease;
}

/* The overlay covers the viewport, so make sure it can never swallow clicks
   while it is fading out. */
.lightbox-leave-active {
  pointer-events: none;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

/* ---------- responsive ---------- */

@media (max-width: 1180px) {
  .gallery-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gallery-tile img,
  .is-landscape .gallery-tile img {
    height: 300px;
  }
}

@media (max-width: 760px) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }

  .gallery-item.is-landscape {
    grid-column: span 1;
  }

  .gallery-tile img,
  .is-landscape .gallery-tile img {
    height: 250px;
  }

  .gallery-filter {
    flex: 1 1 auto;
    justify-content: center;
    padding: 0 14px;
    font-size: 12px;
  }

  .lightbox {
    grid-template-columns: 1fr;
    grid-template-areas:
      "figure"
      "nav";
    align-content: center;
    padding: 72px 18px 24px;
  }

  .lightbox-figure {
    grid-area: figure;
  }

  .lightbox-figure img {
    max-height: 58vh;
  }

  .lightbox-nav {
    grid-area: nav;
  }

  .lightbox-nav.prev {
    justify-self: center;
    margin-right: 60px;
  }

  .lightbox-nav.next {
    justify-self: center;
    margin-left: 60px;
  }

  .lightbox-close {
    top: 18px;
    right: 18px;
  }
}
</style>
